import "@breakout/ui/globals.css"
import { api } from "@breakout/backend/convex/_generated/api"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { useAction } from "convex/react"
import { StrictMode, useState } from "react"
import { createRoot } from "react-dom/client"
import { AdminAuthProvider, useAdminAuth } from "./hooks/useAdminAuth"
import { E2E_MODE } from "./lib/env"
import { Providers } from "./lib/providers"
import { routeTree } from "./routeTree.gen"

const router = createRouter({
	routeTree,
	scrollRestoration: true,
	defaultPreload: "intent", // Preload routes on hover for faster navigation
})

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router
	}
}

const rootElement = document.getElementById("root")
if (!rootElement) {
	throw new Error("Root element not found")
}

createRoot(rootElement).render(
	<StrictMode>
		<Providers>
			<AdminAuthProvider>
				<PasswordGate>
					<RouterProvider router={router} />
				</PasswordGate>
			</AdminAuthProvider>
		</Providers>
	</StrictMode>,
)

// =============================================================================
// Subcomponents (single-use, colocated)
// =============================================================================

function PasswordGate({ children }: { children: React.ReactNode }) {
	const { isAuthenticated, isChecking, setPassword } = useAdminAuth()
	const [inputPassword, setInputPassword] = useState("")
	const [error, setError] = useState("")
	const [isLoading, setIsLoading] = useState(false)
	const verifyPassword = useAction(api.domains.admin.actions.verifyPassword)

	// In E2E mode, bypass password gate entirely
	if (E2E_MODE) {
		return <>{children}</>
	}

	if (isChecking) {
		return (
			<div className="flex min-h-dvh items-center justify-center bg-gray-950">
				<div className="text-gray-400">Loading...</div>
			</div>
		)
	}

	if (isAuthenticated) {
		return <>{children}</>
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		setError("")
		setIsLoading(true)

		try {
			const isValid = await verifyPassword({ password: inputPassword })
			if (isValid) {
				setPassword(inputPassword)
			} else {
				setError("Invalid password")
			}
		} catch {
			setError("Failed to verify password")
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="flex min-h-dvh items-center justify-center bg-gray-950">
			<form className="w-full max-w-sm space-y-4 p-6" onSubmit={handleSubmit}>
				<div>
					<h1 className="font-bold text-2xl text-white">Breakout Admin</h1>
					<p className="mt-1 text-gray-400 text-sm" id="password-hint">
						Enter password to continue
					</p>
				</div>

				<label className="sr-only" htmlFor="admin-password">
					Password
				</label>
				<input
					aria-describedby={error ? "password-hint password-error" : "password-hint"}
					autoComplete="current-password"
					className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
					id="admin-password"
					onChange={e => setInputPassword(e.target.value)}
					placeholder="Password"
					type="password"
					value={inputPassword}
				/>

				{error && (
					<p className="text-red-400 text-sm" id="password-error" role="alert">
						{error}
					</p>
				)}

				<button
					className="w-full rounded-lg bg-gray-600 px-4 py-2 font-medium text-white hover:bg-gray-500 disabled:opacity-50"
					disabled={isLoading || !inputPassword}
					type="submit"
				>
					{isLoading ? "Verifying..." : "Enter"}
				</button>
			</form>
		</div>
	)
}
